import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import 'react-bootstrap-typeahead/css/Typeahead.bs5.css';

export type LomavisFontOption = {
  value: string;
  label: string;
};

export type LomavisFontTypeaheadProps = {
  id: string;
  options: LomavisFontOption[];
  value: string;
  disabled?: boolean;
  placeholder?: string;
  onFocus?: () => void;
  /**
   * Asked to fetch a family so the menu can show it. `onReady` fires once the
   * face can be drawn with — matching `loadBunnyFont`, which is what the
   * overlay editor passes here.
   */
  onPreview?: (font: LomavisFontOption, onReady?: () => void) => void;
  onSelect: (font: LomavisFontOption) => void;
};

/**
 * One row of the menu, in the family it offers.
 *
 * The family was always set on the row, but nothing fetched it until the
 * pointer landed on that row — so a menu of 1800 web fonts opened with none of
 * them loaded and every name drawn in the same fallback. Asking as the row
 * comes into view is what makes the list a list of typefaces instead of a list
 * of words.
 *
 * Only what is on screen, because the menu holds 100 results and shows about
 * ten; the rest are asked for as they are scrolled to.
 */
const FontMenuRow: React.FC<{
  font: LomavisFontOption;
  onPreview?: (font: LomavisFontOption, onReady?: () => void) => void;
}> = ({ font, onPreview }) => {
  const rowRef = useRef<HTMLSpanElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!onPreview) return;
    let active = true;
    const request = (): void => {
      onPreview(font, () => {
        // The browser does repaint text when a matching face lands, but the
        // row saying so itself is what makes it verifiable from a test.
        if (active) setReady(true);
      });
    };

    const row = rowRef.current;
    if (!row || typeof IntersectionObserver === 'undefined') {
      request();
      return () => {
        active = false;
      };
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        observer.disconnect();
        request();
      }
    });
    observer.observe(row);

    return () => {
      active = false;
      observer.disconnect();
    };
  }, [font, onPreview]);

  return (
    <span
      ref={rowRef}
      data-font-ready={ready}
      style={{ fontFamily: font.value }}
    >
      {font.label}
    </span>
  );
};

/**
 * A controlled, single-select font picker for local font catalogs. It keeps
 * the selected value readable in its own typeface and can lazy-load previews
 * through `onPreview` without changing the selected value.
 */
export const LomavisFontTypeahead: React.FC<LomavisFontTypeaheadProps> = ({
  id,
  options,
  value,
  disabled = false,
  placeholder = 'Search fonts…',
  onFocus,
  onPreview,
  onSelect
}) => {
  // The catalogue runs to some 1800 families and this component re-renders
  // with the panel around it, so the search for the current one is worth
  // keeping.
  const selectedOption = useMemo(
    () =>
      options.find((font) => font.value === value) ?? {
        value,
        label: value
      },
    [options, value]
  );
  const [selected, setSelected] = useState<LomavisFontOption[]>([
    selectedOption
  ]);
  const previousValueRef = useRef(value);

  useEffect(() => {
    if (previousValueRef.current === value) return;
    previousValueRef.current = value;
    setSelected([selectedOption]);
  }, [selectedOption, value]);

  return (
    <Typeahead
      id={id}
      labelKey="label"
      options={options}
      selected={selected}
      disabled={disabled}
      minLength={0}
      maxResults={100}
      maxHeight="min(300px, calc(100vh - 1rem))"
      paginate
      flip
      positionFixed
      placeholder={placeholder}
      onFocus={() => {
        // A selected value is rendered as the typeahead query. Clear it while
        // searching so the menu starts with the full font catalogue.
        setSelected([]);
        onFocus?.();
      }}
      onBlur={() => setSelected([selectedOption])}
      onInputChange={() => setSelected([])}
      onChange={(nextSelected) => {
        const font = nextSelected[0] as LomavisFontOption | undefined;
        if (!font) return;
        setSelected([font]);
        onSelect(font);
      }}
      renderMenuItemChildren={(option) => (
        <FontMenuRow font={option as LomavisFontOption} onPreview={onPreview} />
      )}
      renderInput={({ inputRef, referenceElementRef, ...inputProps }) => (
        <input
          {...inputProps}
          ref={(node) => {
            inputRef(node);
            referenceElementRef(node);
          }}
          className={`${inputProps.className ?? ''} form-control`}
          style={{ ...inputProps.style, fontFamily: value }}
        />
      )}
    />
  );
};
